import { AppState } from 'src/AppState/AppState';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { action, computed, observable, makeAutoObservable } from 'mobx';
//@ts-expect-error
import natsort from 'javascript-natural-sort';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { OpenapiProxyStaffSelectionResultGroupParamsType } from 'src/api_openapi/generated/openapi_proxy_staff_selection_result_group';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';

const StatusIO = t.union([t.literal('open'), t.literal('resulted'), t.literal('settled')]);

type StatusType = t.TypeOf<typeof StatusIO>;
const isStatus = createGuard(StatusIO);

type BodyDataType = OpenapiProxyStaffSelectionResultGroupParamsType['requestBody']['data'];

const MARKET_STATES = {
    open: {
        filterLabel: 'Not resulted',
        label: 'Open',
        filter: (marketState: string | null): boolean => marketState === 'open' || marketState === 'suspended',
    },
    resulted: {
        filterLabel: 'Resulted',
        label: 'Resulted',
        filter: (marketState: string | null): boolean => marketState === 'resulted',
    },
    settled: {
        filterLabel: 'Settled',
        label: 'Settled',
        filter: (marketState: string | null): boolean => marketState === 'settled',
    },
};

const convertStringToStatus = (value: string | null): StatusType => {
    if (value === null) {
        return 'open';
    }

    if (isStatus(value)) {
        return value;
    }

    console.warn('Invalid parameter from routing "events"."filterResult" -> ', value);
    return 'open';
};

export class EventResultsMarketState {
    @observable private modeFlag: 'r4' | 'dividends' | null = null;

    public constructor(
        private readonly appState: AppState,
        private readonly marketId: MarketId
    ) {
        makeAutoObservable(this);
    }

    public get mode(): 'r4' | 'dividends' | null {
        return this.modeFlag;
    }

    @action public toogleR4 = (): void => {
        if (this.modeFlag === 'r4') {
            this.modeFlag = null;
        } else {
            this.appState.routerBackoffice.setParamInNamespaceNumber('events', 'market', this.marketId.toOldId());
            this.modeFlag = 'r4';
        }
    };

    @action public toogleDividends = (): void => {
        if (this.modeFlag === 'dividends') {
            this.modeFlag = null;
        } else {
            this.appState.routerBackoffice.setParamInNamespaceNumber('events', 'market', this.marketId.toOldId());
            this.modeFlag = 'dividends';
        }
    };

    @computed public get isOpen(): boolean {
        const marketId = this.appState.routerBackoffice.getParametersNumber('events', 'market');
        return marketId === this.marketId.toOldId();
    }

    @action public toggleMarket = (): void => {
        if (this.isOpen) {
            if (this.modeFlag === null) {
                this.appState.routerBackoffice.removeParamInNamespace('events', 'market');
            } else {
                this.modeFlag = null;
            }
        } else {
            this.appState.routerBackoffice.setParamInNamespaceNumber('events', 'market', this.marketId.toOldId());
        }
    };
}

interface AvailableItemType {
    id: string;
    name: string;
}

export class EventResultsState {
    @observable private hideLosers: boolean | undefined;
    @observable public isConfirmPopupOpened: boolean = false;
    @observable public isSettlingInProgress: boolean = false;

    private readonly appState: AppState;
    private readonly models: MobxMapAutoNew<MarketId, EventResultsMarketState>;

    public constructor() {
        this.appState = $appState;

        this.models = new MobxMapAutoNew((marketId: MarketId) => new EventResultsMarketState(this.appState, marketId));
        makeAutoObservable(this);
    }

    @computed public get filter(): StatusType {
        const value = this.appState.routerBackoffice.getParametersString('events', 'filterResult');
        return convertStringToStatus(value);
    }

    public get availableMarkets(): Array<AvailableItemType> {
        const out: Array<AvailableItemType> = [];
        for (const [key, item] of Object.entries(MARKET_STATES)) {
            out.push({
                id: key,
                name: item.filterLabel,
            });
        }

        return out;
    }

    @action public onChangeFilter = (id: string): void => {
        const status = convertStringToStatus(id);
        this.appState.routerBackoffice.setParamInNamespace('events', 'filterResult', status);
    };

    @action public openVoidPopup = (): void => {
        this.isConfirmPopupOpened = true;
    };
    @action public cancelVoidPopup = (): void => {
        this.isConfirmPopupOpened = false;
    };

    public get shouldHideLosers(): boolean {
        return this.hideLosers ?? false;
    }

    public toogleHideLosers = (): void => {
        this.hideLosers = !this.shouldHideLosers;
    };

    public marketLabel(status: StatusType): string {
        if (isStatus(status)) {
            return MARKET_STATES[status].label;
        }

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.info(`unknown status - ${status}`);
        return '-- unknown --';
    }

    public getMarketModelView = (marketId: MarketId): EventResultsMarketState => this.models.get(marketId);

    private buildBodyDataPerMarket = (selections: Array<SelectionModel>): Record<number, BodyDataType> => {
        const requestQuePerMarket: Record<number, BodyDataType> = {};
        for (const selection of selections) {
            const marketId = selection.marketId2.toOldId();
            const selectionData = {
                id: selection.id2.toOldId(),
                result: {
                    type: 'void',
                    placeReductions: [],
                    isDraft: false,
                },
            };

            if (requestQuePerMarket[marketId] === undefined) {
                requestQuePerMarket[marketId] = [selectionData];
            } else {
                requestQuePerMarket[marketId].push(selectionData);
            }
        }
        return requestQuePerMarket;
    };

    public setAllOpenMarketsVoidAndSettled = async (
        selections: Array<SelectionModel>,
        event_id: EventId
    ): Promise<void> => {
        this.isSettlingInProgress = true;
        const requestQuePerMarket = this.buildBodyDataPerMarket(selections);
        const responseDataErorrs: Array<number> = [];

        for (const currentMarketId in requestQuePerMarket) {
            if (this.isConfirmPopupOpened === false) {
                break;
            }
            const market_id = parseInt(currentMarketId, 10);
            const data = requestQuePerMarket[market_id];
            if (data === undefined) {
                return;
            }
            const result = await this.appState.sdkStaff.selection_result_and_settle_group({
                requestBody: {
                    data,
                    event_id: event_id.toOldId(),
                    market_id,
                },
            });

            await timeout(800); //Timeout to avoid server overload

            if (result.type === 'EventChangeResponseError') {
                responseDataErorrs.push(market_id);
                console.error(`selection_result_group error, with market id: ${market_id}`, result);
            }
        }

        if (responseDataErorrs.length !== 0) {
            this.appState.reduxAction.addError(`Settled markets with error: ${responseDataErorrs.join(', ')}`);
        }

        this.isConfirmPopupOpened = false;
        this.isSettlingInProgress = false;
    };

    public getSelections(markets: Array<MarketId>): Array<SelectionModel> {
        const selectionsWithNoResult: Array<SelectionModel> = [];
        for (const marketId of markets) {
            const selections = marketId.getModel()?.selections.filter((model) => model.resultType === null) ?? [];
            selectionsWithNoResult.push(...selections);
        }

        return selectionsWithNoResult;
    }
}

const isMarketSettled = (market: MarketId): boolean => {
    const selections = market.getModel()?.selections ?? [];
    const selectionsSettled = selections.filter((sel) => {
        return sel.getRawData().result?.isDraft === false;
    });
    return selections.length === selectionsSettled.length;
};

export const getMarketStatus = (marketId: MarketId): string | null => {
    const marketState = marketId.getModel()?.getRawData().state ?? null;
    const isSettled = isMarketSettled(marketId);
    if (marketState === 'resulted') {
        if (isSettled === true) {
            return 'settled';
        }
    }
    return marketState;
};

export function getMarkets(eventId: EventId, appState: AppState): Array<MarketId> {
    const markets = appState.sdkStaff.models.getEventMarketsAll(eventId.toOldId()) ?? [];
    const filterType = appState.routerBackoffice.getParametersString('events', 'filterResult');
    const filterValue = convertStringToStatus(filterType);

    return markets
        .sort((a, b) => natsort(a.name, b.name))
        .filter((market: EventMarketItemType): boolean => {
            const marketStatus = getMarketStatus(market.id2);
            return MARKET_STATES[filterValue].filter(marketStatus);
        })
        .map((item) => item.id2);
}
